import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import { DynamicListChartContainer } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import PhoneNumber from 'components/field/PhoneNumber';
import { InputMessageContainer } from 'components/field/styled/InputCommon.styled';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { routeMapping } from 'pages/Shipments/utils/shipmentStatusMapping';

import { ENUMS, REPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faExclamationCircle, faSearch } from '@fortawesome/pro-solid-svg-icons';

import * as S from './CustomerDetails.styled';

const CustomerDetailsTab = () => {
	const [phoneDefaultFilter, setPhoneDefaultFilter] = useState([]);
	const [submittedPhone, setSubmittedPhone] = useState(null);

	const schema = useMemo(() => yup.object().shape({ phone: yup.string().required() }).required(), []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: { phone: null }
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty }
	} = formMethods;

	const body = useMemo(() => ({ phone: submittedPhone }), [submittedPhone]);

	const onSubmit = (data) => {
		setPhoneDefaultFilter(() => [
			{
				column: { Header: 'phone', accessor: 'phone', width: 100, filterType: FilterType.string },
				action: FilterAction.is,
				value: data.phone
			}
		]);
		setSubmittedPhone(data.phone);
	};

	useEffect(() => {
		const keyDownHandler = (e) => {
			if (e.key === 'Enter') {
				handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		};
		document.addEventListener('keydown', keyDownHandler);
		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, [handleSubmit, onSubmit]);

	return (
		<WidgetPageContainer>
			<div>
				<S.PhoneFilterContainer>
					<div>
						<PhoneNumber
							name="phone"
							placeholder="phoneNumber"
							onChange={(value) => {
								setValue('phone', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('phone')}
						/>
					</div>
					<span className="mx-1" />
					<Button
						label="search"
						variant="blue"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						icon={faSearch}
						disabled={!isDirty}
					/>
				</S.PhoneFilterContainer>
				{!watch('phone') && (
					<InputMessageContainer className="error-message-container string-input-error-message-container">
						<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
						<Typography variant="caption" color="var(--red)" noWrap>
							alert.requiredField
						</Typography>
					</InputMessageContainer>
				)}
			</div>
			<WidgetWrapper widgetId={'customerSummary'}>
				<CustomerSummary body={body} phoneDefaultFilter={phoneDefaultFilter} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'analysis.customerOrdersDescription'}>
				<CustomerOrders phoneDefaultFilter={phoneDefaultFilter} phone={body.phone} />
			</WidgetWrapper>
		</WidgetPageContainer>
	);
};

const CustomerSummary = ({ body, phoneDefaultFilter }) => {
	const navigate = useNavigate();

	const [filter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const cardsConfig = [
		{
			accessor: 'total',
			label: 'total',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--fa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)',
			onButtonClick: () => {
				navigate(routeMapping.allOrders, {
					state: {
						defaultFilters: [
							{
								column: {
									Header: 'orderDate',
									accessor: 'createdDate',
									width: 100,
									filterType: FilterType.date
								},
								action: 'between',
								value: filter
							},
							...(phoneDefaultFilter.length !== 0 ? phoneDefaultFilter : [])
						]
					}
				});
			}
		},
		{
			accessor: 'processing',
			label: 'processing',
			customIcon: 'fa-regular-loader-circle-bolt',
			backgroundColor: 'var(--fa-regular-loader-circle-bolt-bgColor)',
			color: 'var(--fa-regular-loader-circle-bolt-color)',
			onButtonClick: () => {
				navigate(routeMapping.pending, {
					state: {
						defaultFilters: [...(phoneDefaultFilter.length !== 0 ? phoneDefaultFilter : [])]
					}
				});
			}
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)',
			onButtonClick: () => {
				navigate(routeMapping.delivered, {
					state: {
						defaultFilters: [
							{
								column: {
									Header: 'orderDate',
									accessor: 'createdDate',
									filterType: FilterType.date
								},
								action: FilterAction.between,
								value: {
									start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
									end: dayjs().endOf('day').toISOString()
								}
							},
							...(phoneDefaultFilter.length !== 0 ? phoneDefaultFilter : [])
						]
					}
				});
			}
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)',
			onButtonClick: () => {
				navigate(routeMapping.returned, {
					state: {
						defaultFilters: [
							{
								column: {
									Header: 'status',
									accessor: 'status',
									filterType: FilterType.string
								},
								action: FilterAction.is,
								value: ['returned', 'reverseReturned']
							},
							...(phoneDefaultFilter.length !== 0 ? phoneDefaultFilter : [])
						]
					}
				});
			}
		},
		{
			accessor: 'canceled',
			label: 'canceled',
			customIcon: 'fa-regular-box-circle-xmark',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)',
			onButtonClick: () => {
				navigate(routeMapping.canceled, {
					state: {
						defaultFilters: [...(phoneDefaultFilter.length !== 0 ? phoneDefaultFilter : [])]
					}
				});
			}
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.CUSTOMER_DETAILS.GET_CUSTOMER_ACTIVITY,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader title={'customerSummary'} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const CustomerOrders = ({ phoneDefaultFilter }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter, phoneDefaultFilter]);

	const columns = useMemo(
		() => [
			{
				Header: 'otoOrderID',
				accessor: 'ID',
				filterType: FilterType.number,
				width: 70,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 70,
				showOnMobile: true,
				showCopyButton: true
			},
			{
				Header: 'orderDate',
				accessor: 'createdAt',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},

			{ Header: 'customerName', accessor: 'name', filterType: FilterType.string, showOnMobile: true },
			{ Header: 'customerPhone', accessor: 'phone', filterType: FilterType.string },
			{ Header: 'customerAddress', accessor: 'address', filterType: FilterType.string },
			{ Header: 'city', accessor: 'cityName', width: 100, filterType: FilterType.multiSelection, show: false },
			{
				Header: 'country',
				accessor: 'countryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100,
				show: false
			},
			{ Header: 'deliveryName', accessor: 'deliveryName', width: 100, filterType: FilterType.string },
			{
				Header: 'grandTotal',
				accessor: 'grandTotal',
				renderCell: ListComponents.CURRENCY,

				filterType: FilterType.float
			},
			{
				Header: 'totalDue',
				accessor: 'totalDue',
				renderCell: ListComponents.CURRENCY,

				filterType: FilterType.float
			},
			{
				Header: 'subtotal',
				accessor: 'subTotal',
				filterType: FilterType.float,
				width: 90,
				show: false,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'shippingAmount',
				accessor: 'shippingAmount',
				width: 100,
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'status',
				accessor: 'status',
				width: 200,
				filterType: FilterType.string,
				renderCell: ListComponents.STATUS
			}
		],
		[]
	);

	const defaultFilter = useMemo(
		() => [
			{
				column: columns.find((col) => col.accessor === 'createdAt'),
				action: FilterAction.between,
				...(body && {
					value: {
						start: body.from,
						end: body.to
					}
				})
			},
			...phoneDefaultFilter
		],
		[filter, phoneDefaultFilter]
	);

	return (
		<Card>
			<ChartHeader
				title={'analysis.customerOrders'}
				titleTooltip="analysis.customerOrdersDescription"
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<DynamicListChartContainer>
				{phoneDefaultFilter.length === 0 ? (
					<NoRecordsFound subTitle="customerOrders.notFoundDescription" />
				) : (
					<DynamicList
						id={DYNAMICLISTS.LAST_10_ORDERS}
						columns={columns}
						defaultFilters={defaultFilter}
						fetchUrl={REPORT.CUSTOMER_DETAILS.GET_LAST_10_ORDER}
						showHeader={false}
						exportName="last10Orders"
					/>
				)}
			</DynamicListChartContainer>
		</Card>
	);
};

export default CustomerDetailsTab;
